import { formatUnits, parseUnits as vParseUnits } from 'viem'
import type { TNormalizedBN, TNumberish } from '../types/mixed'
import { MAX_UINT_256 } from './constants'
import { isZero } from './tools.is'

/***************************************************************************
 ** Formats very small numbers using subscript notation for leading zeros.
 ** Example: 0.000000000004 becomes "0.0₁₀4" (10 zeros shown as subscript)
 ** Only applies when there are 4+ leading zeros after decimal point.
 **************************************************************************/
const SUBSCRIPT_DIGITS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉']

function toSubscript(num: number): string {
  return num
    .toString()
    .split('')
    .map((digit) => SUBSCRIPT_DIGITS[parseInt(digit, 10)])
    .join('')
}

export function formatWithSubscriptZeros(amount: number, maxSignificantDigits = 4): string | null {
  if (amount <= 0 || amount >= 0.0001) {
    return null // Don't use subscript notation for these values
  }

  // Convert to string to count leading zeros
  const str = amount.toFixed(20) // Use high precision
  const decimalIndex = str.indexOf('.')
  if (decimalIndex === -1) return null

  const afterDecimal = str.slice(decimalIndex + 1)

  // Count leading zeros
  let zeroCount = 0
  for (const char of afterDecimal) {
    if (char === '0') {
      zeroCount++
    } else {
      break
    }
  }

  // Only use subscript if 4+ leading zeros
  if (zeroCount < 4) {
    return null
  }

  // Get significant digits after the zeros
  const significantPart = afterDecimal.slice(zeroCount)
  const significantDigits = significantPart.slice(0, maxSignificantDigits).replace(/0+$/, '') || '0'

  // Format: 0.0₍zeroCount-1₎significantDigits
  // We show one zero explicitly, then subscript for the rest
  return `0.0${toSubscript(zeroCount - 1)}${significantDigits}`
}

export const DefaultTNormalizedBN: TNormalizedBN = { raw: 0n, normalized: 0, display: '0', decimals: 18 }

export const simpleToExact = (value: number | string = 0, d = 18): bigint => {
  return parseUnits(value || '0', d)
}

export const exactToSimple = (bn?: bigint | string | number, scale?: number) => {
  // BigNumbers can be returned by wagmi's query cache. This should happen
  // once per-browser sesion until wagmi's localStorage is cleared or overwritten.
  let res = bn
  if (typeof bn === 'object') {
    if ((bn as { hex?: string }).hex) {
      res = BigInt((bn as { hex: string }).hex)
    } else {
      console.log('unknown object type', bn, Object.entries(bn))
      return 0
    }
  }
  return Number.parseFloat(formatUnits(BigInt(res ?? 0), scale ?? 18))
}

/***************************************************************************
 ** Bunch of function using the power of the browsers and standard functions
 ** to correctly format bigNumbers, currency and date
 **************************************************************************/
export const toBigInt = (amount?: TNumberish): bigint => {
  if (amount === undefined || amount === null) {
    return 0n
  }

  if (typeof amount === 'bigint') {
    return amount
  }

  const asString = String(amount).trim()
  if (asString === '') {
    return 0n
  }

  const normalized = asString.includes('e') || asString.includes('E') ? eToNumber(asString) : asString
  const integerPart = normalized.includes('.') ? normalized.split('.')[0] : normalized

  if (integerPart === '' || integerPart === '-' || integerPart === '+') {
    return 0n
  }

  return BigInt(integerPart)
}

export function toBigNumberAsAmount(bnAmount = 0n, decimals = 18, decimalsToDisplay = 2, symbol = ''): string {
  let locale = 'en-US'
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || 'fr-FR'
  }
  const locales = []
  locales.push('en-US')
  locales.push(locale)

  let symbolWithPrefix = symbol
  if (symbol.length > 0 && symbol !== '%') {
    symbolWithPrefix = ` ${symbol}`
  }

  if (bnAmount === 0n) {
    return `0${symbolWithPrefix}`
  }
  if (bnAmount === MAX_UINT_256) {
    return `∞${symbolWithPrefix}`
  }

  const formatedAmount = formatUnits(bnAmount, decimals)
  return `${new Intl.NumberFormat([locale, 'en-US'], {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalsToDisplay
  }).format(Number(formatedAmount))}${symbolWithPrefix}`
}

export const toNormalizedValue = (v: bigint, d?: number): number => {
  return Number(formatUnits(v, d ?? 18))
}

export const toNormalizedAmount = (v: bigint, d?: number): string => {
  return formatAmount(toNormalizedValue(v, d ?? 18), 6, 6)
}

export function toNormalizedBN(value: TNumberish, decimals: number): TNormalizedBN {
  return {
    raw: toBigInt(value),
    normalized: Number(formatUnits(toBigInt(value), decimals ?? 18)),
    display: formatUnits(toBigInt(value), decimals ?? 18),
    decimals
  }
}
export const zeroNormalizedBN: TNormalizedBN = toNormalizedBN(0, 18)

export function fromNormalized(value: number | string, decimals = 18): bigint {
  return vParseUnits(eToNumber(String(value)), decimals)
}

export function parseUnits(value: TNumberish, decimals = 18): bigint {
  const valueAsNumber = Number(value)
  return vParseUnits(`${valueAsNumber}`, decimals)
}

type TAmountOptions = {
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  displayDigits?: number
  shouldDisplaySymbol?: boolean
  shouldCompactValue?: boolean
}

export type TAmount = {
  value: bigint | number
  decimals: number | bigint
  symbol?: string
  options?: TAmountOptions
}

type TFormatCurrencyWithPrecision = {
  amount: number
  maxFractionDigits: number
  intlOptions: Intl.NumberFormatOptions
  locales: string[]
  symbol: string
}

export const defaultOptions: TAmountOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  displayDigits: 0,
  shouldDisplaySymbol: true,
  shouldCompactValue: true
}

function assertValidNumber(value: number | undefined, defaultValue: number, label: string): number {
  if (value === undefined) {
    return defaultValue
  }
  if (value < 0) {
    console.warn(`formatAmount: ${label} should be positive.`)
    return defaultValue
  }
  if (value > 18) {
    console.warn(`formatAmount: ${label} should be less than 18.`)
    return 18
  }
  if (Number.isNaN(value)) {
    console.warn(`formatAmount: ${label} is NaN.`)
    return defaultValue
  }
  if (!Number.isSafeInteger(value)) {
    console.warn(`formatAmount: ${label} should be an integer.`)
    return defaultValue
  }
  return value
}

function assignOptions(options?: TAmountOptions): TAmountOptions {
  if (!options) {
    return defaultOptions
  }

  /**********************************************************************************************
   ** We need to ensure that displayDigits is a valid number. It can be any positive integer
   ** between 0 and 18. If the value is invalid, we display a warning and set the value to the
   ** default value (0 or 18).
   **********************************************************************************************/
  options.displayDigits = assertValidNumber(options?.displayDigits, 0, 'displayDigits')

  /**********************************************************************************************
   ** We need to ensure that minimumFractionDigits is a valid number. It can be any positive
   ** integer between 0 and 18. If the value is invalid, we display a warning and set the value to
   ** the default value (2 or 18).
   **********************************************************************************************/
  options.minimumFractionDigits = assertValidNumber(options?.minimumFractionDigits, 2, 'minimumFractionDigits')

  /**********************************************************************************************
   ** We need to ensure that maximumFractionDigits is a valid number. It can be any positive
   ** integer between 0 and 18. If the value is invalid, we display a warning and set the value to
   ** the default value (2 or 18).
   **********************************************************************************************/
  options.maximumFractionDigits = assertValidNumber(options?.maximumFractionDigits, 2, 'maximumFractionDigits')

  /**********************************************************************************************
   ** We need to ensure that maximumFractionDigits is always bigger or equal to
   ** minimumFractionDigits, otherwise we set them as equal.
   **********************************************************************************************/
  if (options.maximumFractionDigits < options.minimumFractionDigits) {
    options.maximumFractionDigits = options.minimumFractionDigits
  }

  options.shouldDisplaySymbol ??= true
  options.shouldCompactValue ??= true

  return options
}

function formatCurrencyWithPrecision({
  amount,
  maxFractionDigits,
  intlOptions,
  locales,
  symbol
}: TFormatCurrencyWithPrecision): string {
  return new Intl.NumberFormat(locales, {
    ...intlOptions,
    maximumFractionDigits: Math.max(maxFractionDigits, intlOptions.maximumFractionDigits || maxFractionDigits)
  })
    .format(amount)
    .replace('EUR', symbol)
}

export function formatLocalAmount(amount: number, decimals: number, symbol: string, options: TAmountOptions): string {
  /**********************************************************************************************
   ** Define the normalized elements.
   ** We use a few tricks here to get the benefits of the intl package and correct formating no
   ** matter the provided local.
   ** - Fallback formatting is set to `fr-FR`
   ** - If symbol is USD, then we will display as `123,79 $` or `$123.79` (US)
   ** - If smbol is percent, then we will display as `12 %` or `12%` (US)
   ** - If symbol is any other token, we will display as `123,79 USDC` or `USDC 123.79` (US)
   **********************************************************************************************/
  const locales = resolveLocales()

  const { shouldDisplaySymbol, shouldCompactValue, ...rest } = options
  const intlOptions: Intl.NumberFormatOptions = rest
  let isPercent = false
  if (symbol && shouldDisplaySymbol) {
    const uppercaseSymbol = String(symbol).toLocaleUpperCase()
    const symbolToFormat = uppercaseSymbol === 'USD' ? 'USD' : 'EUR'
    intlOptions.style = uppercaseSymbol === 'PERCENT' ? 'percent' : 'currency'
    intlOptions.currency = symbolToFormat
    intlOptions.currencyDisplay = symbolToFormat === 'EUR' ? 'code' : 'narrowSymbol'
    isPercent = uppercaseSymbol === 'PERCENT'
  }

  if (isPercent && amount > 5 && shouldCompactValue) {
    return `> ${new Intl.NumberFormat(locales, intlOptions).format(5).replace('EUR', symbol)}`
  }

  /**********************************************************************************************
   ** If the amount is above 10k, we will format it to short notation:
   ** - 123947 would be `123,95 k` or  `123.95K` (US)
   ** - 267839372 would be `267,84 M` or  `267.84M` (US)
   **********************************************************************************************/
  if (amount > 10_000 && shouldCompactValue) {
    return new Intl.NumberFormat(locales, {
      ...intlOptions,
      notation: 'compact',
      compactDisplay: 'short'
    })
      .format(amount)
      .replace('EUR', symbol)
  }

  /**********************************************************************************************
   ** If the amount is very small, we adjust the decimals to try to display something, up to
   ** "decimals" number of decimals. For very small numbers with 4+ leading zeros, we use
   ** subscript notation (e.g., 0.0₁₀4 instead of 0.000000000004).
   **********************************************************************************************/
  if (amount < 0.01) {
    if (isPercent) {
      return formatCurrencyWithPrecision({
        amount,
        maxFractionDigits: 2,
        intlOptions,
        locales,
        symbol
      })
    }

    // Try subscript notation for very small numbers (4+ leading zeros)
    const subscriptFormatted = formatWithSubscriptZeros(amount)
    if (subscriptFormatted) {
      const displaySymbol = symbol.toUpperCase() === 'PERCENT' ? '%' : symbol
      const symbolSuffix = symbol && options.shouldDisplaySymbol ? ` ${displaySymbol}` : ''
      return `${subscriptFormatted}${symbolSuffix}`
    }

    if (amount > 0.00000001) {
      return formatCurrencyWithPrecision({
        amount,
        maxFractionDigits: 8,
        intlOptions,
        locales,
        symbol
      })
    }
    if (amount > 0.000000000001) {
      return formatCurrencyWithPrecision({
        amount,
        maxFractionDigits: 12,
        intlOptions,
        locales,
        symbol
      })
    }
    return formatCurrencyWithPrecision({
      amount,
      maxFractionDigits: decimals,
      intlOptions,
      locales,
      symbol
    })
  }
  return new Intl.NumberFormat(locales, intlOptions).format(amount).replace('EUR', symbol)
}

export function formatTAmount(props: TAmount): string {
  const { value } = props
  const options = assignOptions(props.options)
  const decimals = assertValidNumber(Number(props.decimals), 18, 'decimals')
  let amount = 0
  if (typeof value === 'bigint') {
    amount = toNormalizedValue(toBigInt(value), decimals)
  } else if (typeof value === 'number' && !Number.isNaN(value)) {
    amount = value
  }

  if (isZero(amount)) {
    return formatLocalAmount(0, 0, props.symbol || '', options)
  }
  if (!Number.isFinite(amount)) {
    return '∞'
  }

  // Use subscript notation for very small numbers (4+ leading zeros)
  const subscriptFormatted = formatWithSubscriptZeros(amount)
  if (subscriptFormatted) {
    const symbol = props.symbol || ''
    const displaySymbol = symbol.toUpperCase() === 'PERCENT' ? '%' : symbol
    const symbolSuffix = symbol && options.shouldDisplaySymbol ? ` ${displaySymbol}` : ''
    return `${subscriptFormatted}${symbolSuffix}`
  }

  return formatLocalAmount(amount, decimals, props.symbol || '', options)
}

/***************************************************************************
 ** Bunch of function using the power of the browsers and standard functions
 ** to correctly format numbers, currency and date
 **************************************************************************/
export function formatAmount(
  amount: number | string,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  displayDigits = 0,
  options?: {
    locales?: string[]
  }
): string {
  let locale = 'en-US'
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || 'fr-FR'
  }
  const locales = []
  if (options?.locales) {
    locales.push(...options.locales)
  }
  locales.push('en-US')
  locales.push(locale)
  if (maximumFractionDigits < minimumFractionDigits) {
    maximumFractionDigits = minimumFractionDigits
  }
  if (!amount) {
    amount = 0
  }
  if (typeof amount === 'string') {
    amount = Number(amount)
  }
  if (Number.isNaN(amount)) {
    amount = 0
  }
  let formattedAmount = new Intl.NumberFormat(locales, {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(amount)

  if (displayDigits > 0 && formattedAmount.length > displayDigits) {
    const leftSide = formattedAmount.slice(0, Math.ceil(displayDigits / 2))
    const rightSide = formattedAmount.slice(-Math.floor(displayDigits / 2))
    formattedAmount = `${leftSide}...${rightSide}`
  }

  return formattedAmount
}

export function parseAmount(stringNumber: string, providedLocales?: string[]): number {
  let locale = 'en-US'
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || 'fr-FR'
  }
  const locales = []
  if (providedLocales) {
    locales.push(...providedLocales)
  }
  locales.push('en-US')
  locales.push(locale)

  const thousandSeparator = Intl.NumberFormat(locales)
    .format(11111)
    .replace(/\p{Number}/gu, '')
  const decimalSeparator = Intl.NumberFormat(locales)
    .format(1.1)
    .replace(/\p{Number}/gu, '')

  return parseFloat(
    stringNumber
      .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
      .replace(new RegExp('\\' + decimalSeparator), '.')
  )
}

export function formatCurrency(amount: number, decimals = 2): string {
  let locale = 'fr-FR'
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || 'fr-FR'
  }
  return new Intl.NumberFormat([locale, 'en-US'], {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  }).format(amount)
}

export function formatWithUnit(
  amount: number,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2,
  options?: {
    locales?: string[]
  }
): string {
  let locale = 'fr-FR'
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || 'fr-FR'
  }
  if (maximumFractionDigits < minimumFractionDigits) {
    maximumFractionDigits = minimumFractionDigits
  }
  return new Intl.NumberFormat(options?.locales || [locale, 'en-US'], {
    minimumFractionDigits,
    maximumFractionDigits,
    notation: 'compact',
    compactDisplay: 'short',
    unitDisplay: 'short'
  }).format(amount)
}

export function formatUSD(n: number, min = 2, max = 2): string {
  return `$${formatAmount(n || 0, min, max)}`
}

export function formatUSDWithThreshold(n: number, min = 2, max = 2): string {
  if (n > 0 && n < 0.01) {
    return '< $0.01'
  }
  return formatUSD(n, min, max)
}

function formatToSignificantDigits(value: number, significantDigits = 3): string {
  return new Intl.NumberFormat(resolveLocales(), {
    minimumSignificantDigits: 1,
    maximumSignificantDigits: significantDigits
  }).format(value)
}

export function formatPercent(n: number, min?: number, max?: number, upperLimit = 500): string {
  const safeN = Number.isFinite(n) ? n : 0
  const hasFixedPrecisionOverride = min !== undefined || max !== undefined
  const resolvedMin = min ?? 2
  const resolvedMax = max ?? 2

  if (safeN >= upperLimit) {
    return hasFixedPrecisionOverride
      ? `≥ ${formatAmount(upperLimit, resolvedMin, resolvedMax)}%`
      : `≥ ${formatToSignificantDigits(upperLimit)}%`
  }
  return hasFixedPrecisionOverride
    ? `${formatAmount(safeN, resolvedMin, resolvedMax)}%`
    : `${formatToSignificantDigits(safeN)}%`
}

export function formatAllocationPercent(value: number, options?: { locales?: string[] }): string {
  const safeValue = Number.isFinite(value) ? value : 0
  const fractionDigits = resolveSignificantFractionDigits(safeValue)
  const formatter = new Intl.NumberFormat(resolveLocales(options), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })
  return `${formatter.format(safeValue)}%`
}

function resolveLocales(options?: { locales?: string[] }): string[] {
  let locale = 'en-US'
  if (typeof navigator !== 'undefined') {
    locale = navigator.language || 'fr-FR'
  }
  const locales = []
  if (options?.locales) {
    locales.push(...options.locales)
  }
  locales.push('en-US')
  locales.push(locale)
  return locales
}

export function formatApyDisplay(value: number, options?: { locales?: string[] }): string {
  if (value === Infinity || value === -Infinity) {
    return '∞%'
  }
  const safeValue = Number.isFinite(value) ? value : 0
  const percentValue = safeValue * 100
  if (percentValue >= 500) {
    return '≥ 500%'
  }
  const fractionDigits = resolveApyFractionDigits(percentValue)
  const formatter = new Intl.NumberFormat(resolveLocales(options), {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  })
  return `${formatter.format(percentValue)}%`
}

export function normalizeApyDisplayValue(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  const percentValue = value * 100
  const fractionDigits = resolveApyFractionDigits(percentValue)
  return Number(percentValue.toFixed(fractionDigits))
}

function resolveSignificantFractionDigits(value: number): number {
  const absValue = Math.abs(value)
  const digitsBefore = absValue >= 100 ? 3 : absValue >= 10 ? 2 : 1
  return Math.max(0, 3 - digitsBefore)
}

function resolveApyFractionDigits(value: number): number {
  return resolveSignificantFractionDigits(value)
}

export function formatTvlDisplay(
  value: number,
  options?: {
    locales?: string[]
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }
): string {
  if (value === Infinity || value === -Infinity) {
    return '$∞'
  }
  const safeValue = Number.isFinite(value) ? value : 0
  const absValue = Math.abs(safeValue)
  const locales = resolveLocales(options)
  if (absValue >= 10000) {
    const formatter = new Intl.NumberFormat(locales, {
      notation: 'compact',
      compactDisplay: 'short',
      minimumSignificantDigits: 3,
      maximumSignificantDigits: 3
    })
    return `$${formatter.format(safeValue)}`
  }

  let minimumFractionDigits = options?.minimumFractionDigits
  let maximumFractionDigits = options?.maximumFractionDigits

  if (minimumFractionDigits === undefined || maximumFractionDigits === undefined) {
    minimumFractionDigits = 0
    maximumFractionDigits = 0

    if (absValue < 1) {
      minimumFractionDigits = 2
      maximumFractionDigits = 2
    } else if (absValue < 10) {
      minimumFractionDigits = 2
      maximumFractionDigits = 2
    } else if (absValue < 100) {
      minimumFractionDigits = 1
      maximumFractionDigits = 2
    }
  }

  const formatter = new Intl.NumberFormat(locales, {
    minimumFractionDigits,
    maximumFractionDigits
  })
  return `$${formatter.format(safeValue)}`
}

export function formatCounterValue(amount: number | string, price: number): string {
  if (!amount || !price) {
    return `$${formatAmount(0, 2, 2)}`
  }

  const value = (Number(amount) || 0) * (price || 0)
  if (value > 10000) {
    return `$${formatAmount(value, 0, 0)}`
  }
  return `$${formatAmount(value, 2, 2)}`
}

/******************************************************************
 * Converts e-Notation Numbers to Plain Numbers
 ******************************************************************
 * @function eToNumber(number)
 * @version  1.00
 * @param   {e nottation Number} valid Number in exponent format.
 *          pass number as a string for very large 'e' numbers or with large fractions
 *          (none 'e' number returned as is).
 * @return  {string}  a decimal number string.
 * @author  Mohsen Alyafei
 * @date    17 Jan 2020
 * Note: No check is made for NaN or undefined input numbers.
 *
 *****************************************************************/
export function eToNumber(num: string): string {
  let sign = ''
  // Convert to string and handle negative sign
  num = String(num)
  if (num.charAt(0) === '-') {
    sign = '-'
    num = num.substring(1)
  }
  const arr = num.split(/[e]/gi)
  if (arr.length < 2) {
    return sign + num
  }
  const dot = '.'
  let n = arr[0]
  const exp = +arr[1]
  // Remove leading zeros from n, then remove decimal point to get w
  n = n.replace(/^0+/, '')
  let w = n.replace(dot, '')
  const pos = n.split(dot)[1] ? n.indexOf(dot) + exp : w.length + exp
  const L = pos - w.length
  const s = '' + BigInt(w)
  w = exp >= 0 ? (L >= 0 ? s + '0'.repeat(L) : r()) : pos <= 0 ? '0' + dot + '0'.repeat(Math.abs(pos)) + s : r()
  const V = w.split(dot)
  if ((Number(V[0]) === 0 && Number(V[1]) === 0) || (+w === 0 && +s === 0)) {
    w = '0'
  } //** added 9/10/2021
  return sign + w
  function r(): string {
    return w.replace(new RegExp(`^(.{${pos}})(.)`), `$1${dot}$2`)
  }
}
